// ==========================================================================
// Loan Offering - Modal (first time) + Banner (subsequent)
// Supports both USSD (tel: dialer) and URL (opens in new tab) types
// ==========================================================================

import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import type { LoanOfferingType } from '@/types/alert.types';

/**
 * Centered blocking modal for first-time loan offering.
 */
export function LoanOfferingModal({
  title,
  body,
  offeringType = 'ussd',
  telLink,
  value,
  onDismiss,
}: {
  title: string;
  body: string;
  offeringType?: LoanOfferingType;
  telLink?: string;
  value: string;
  onDismiss: () => void;
}) {
  const { t } = useTranslation();

  // Lock body scroll while modal is open
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 px-4"
      onClick={(e) => e.stopPropagation()}
      onTouchStart={(e) => e.stopPropagation()}
      onTouchMove={(e) => { e.preventDefault(); e.stopPropagation(); }}
      onTouchEnd={(e) => e.stopPropagation()}
    >
      <div className="w-full max-w-sm rounded-2xl bg-white p-6 shadow-xl animate-slide-up">
        <div className="text-center mb-4">
          <span className="text-4xl">🌧️</span>
        </div>

        <h2 className="text-lg font-bold text-gray-900 text-center leading-snug mb-3">
          {title}
        </h2>

        <p className="text-sm text-gray-600 leading-relaxed mb-6">
          {body}
        </p>

        {offeringType === 'url' && value && (
          <a
            href={value}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center justify-center gap-2 w-full py-3 rounded-xl text-white text-sm font-semibold bg-brand-primary"
          >
            <span>🔗</span>
            {t('loanOffering.openLink')}
          </a>
        )}

        {offeringType === 'ussd' && telLink && (
          <a
            href={telLink}
            className="flex items-center justify-center gap-2 w-full py-3 rounded-xl text-white text-sm font-semibold bg-brand-primary"
          >
            <span>📲</span>
            {t('loanOffering.getStarted')}
          </a>
        )}

        {offeringType === 'ussd' && !telLink && value && (
          <div className="w-full py-3 rounded-xl bg-gray-100 text-center">
            <span className="text-sm font-semibold text-gray-900">{t('loanOffering.dial', { code: value })}</span>
          </div>
        )}

        <button
          type="button"
          onClick={onDismiss}
          className="mt-3 w-full py-3 rounded-xl border border-gray-300 bg-white text-sm font-semibold text-gray-700"
        >
          {t('loanOffering.notNow')}
        </button>
      </div>
    </div>
  );
}

/**
 * Minimal top notification banner for subsequent loan offering appearances.
 */
export function LoanOfferingBanner({
  offeringType = 'ussd',
  telLink,
  value,
}: {
  offeringType?: LoanOfferingType;
  telLink?: string;
  value: string;
}) {
  const { t } = useTranslation();
  return (
    <div className="bg-brand-primary rounded-xl px-4 py-3 mb-4 flex items-center justify-between gap-3">
      <div className="flex items-center gap-2 min-w-0">
        <span className="text-lg shrink-0">💰</span>
        <span className="text-xs font-medium text-white truncate">
          {t('loanOffering.bannerTitle')}
        </span>
      </div>
      {offeringType === 'url' && value ? (
        <a
          href={value}
          target="_blank"
          rel="noopener noreferrer"
          className="shrink-0 text-xs font-semibold text-white bg-white/20 rounded-full px-3 py-1.5"
        >
          {t('loanOffering.applyNow')}
        </a>
      ) : telLink ? (
        <a
          href={telLink}
          className="shrink-0 text-xs font-semibold text-white bg-white/20 rounded-full px-3 py-1.5"
        >
          {t('loanOffering.dial', { code: value })}
        </a>
      ) : (
        <span className="shrink-0 text-xs font-semibold text-white bg-white/20 rounded-full px-3 py-1.5">
          {value}
        </span>
      )}
    </div>
  );
}
